import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, ArrowUpRight, Menu, X, Sun, Moon } from "lucide-react";

const LINKS = [
  { label: "Booking Engine", href: "#booking" },
  { label: "Subdomains", href: "#subdomains" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Destinations", href: "#destinations" },
  { label: "B2B Network", href: "#network" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleTheme = () => {
    const el = document.documentElement;
    el.classList.toggle("dark");
    const isDark = el.classList.contains("dark");
    localStorage.setItem("tc_theme", isDark ? "dark" : "light");
    setDark(isDark);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-goldline bg-coal/75 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[90rem] items-center justify-between gap-10 px-6 py-5 sm:px-10 lg:px-16">
        <a href="#top" data-testid="nav-brand-logo" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
            <Compass className="text-gold" size={18} />
          </span>
          <span className="whitespace-nowrap font-serif text-xl font-semibold tracking-tight">
            Tourism<span className="text-gold"> Connect</span>
          </span>
        </a>

        <div className="hidden flex-1 items-center justify-center gap-10 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="whitespace-nowrap text-xs font-medium uppercase tracking-[0.15em] text-muted transition-colors duration-300 hover:text-gold"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <button
            data-testid="theme-toggle-button"
            onClick={toggleTheme}
            aria-label="Toggle light and dark mode"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-muted transition-colors duration-300 hover:border-gold/50 hover:text-gold"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <a data-testid="nav-join-waitlist-button" href="#waitlist" className="btn-gold hidden !px-6 !py-3 !text-xs sm:inline-flex">
            Join B2B Network <ArrowUpRight size={14} />
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-cream transition-colors duration-300 hover:border-gold/50 lg:hidden"
          >
            <Menu size={18} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm lg:hidden"
            />
            <motion.div
              data-testid="mobile-menu-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-50 flex w-72 flex-col border-l border-goldline bg-coal p-8 lg:hidden"
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-lg font-semibold">
                  Tourism<span className="text-gold"> Connect</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="mobile-theme-toggle-button"
                    onClick={toggleTheme}
                    aria-label="Toggle light and dark mode"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-muted"
                  >
                    {dark ? <Sun size={15} /> : <Moon size={15} />}
                  </button>
                  <button
                    data-testid="mobile-menu-close-button"
                    onClick={() => setOpen(false)}
                    aria-label="Close menu"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-cream"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-1">
                {LINKS.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06 }}
                    className="border-b border-line py-4 font-serif text-2xl text-cream transition-colors duration-300 hover:text-gold"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </div>

              <div className="mt-auto space-y-3">
                <a
                  data-testid="mobile-nav-booking-link"
                  href="https://booking.tourism-connect.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-green-500"
                >
                  <span className="live-dot h-1.5 w-1.5 rounded-full bg-green-500" />
                  booking.tourism-connect.com
                </a>
                <a
                  data-testid="mobile-nav-waitlist-button"
                  href="#waitlist"
                  onClick={() => setOpen(false)}
                  className="btn-gold w-full justify-center"
                >
                  Join B2B Network <ArrowUpRight size={14} />
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
